import * as React from 'react';
import { Button as AriaButton, Link as AriaLink, type ButtonProps as AriaButtonProps } from 'react-aria-components';

import { cx, sortCx } from '../../utils/cx';

export const GoogleLogo = ({ colorful, ...props }: React.SVGProps<SVGSVGElement> & { colorful?: boolean }) => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z"
        fill={colorful ? '#4285F4' : 'currentColor'}
      />
      <path
        d="M12.24 24.0008C15.4764 24.0008 18.2058 22.9382 20.1944 21.1039L16.3274 18.1055C15.2516 18.8375 13.8626 19.252 12.2444 19.252C9.11376 19.252 6.45934 17.1399 5.50693 14.3003H1.51648V17.3912C3.55359 21.4434 7.70278 24.0008 12.24 24.0008Z"
        fill={colorful ? '#34A853' : 'currentColor'}
      />
      <path
        d="M5.50253 14.3003C4.99987 12.8099 4.99987 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z"
        fill={colorful ? '#FBBC04' : 'currentColor'}
      />
      <path
        d="M12.24 4.74966C13.9508 4.7232 15.6043 5.36697 16.8433 6.54867L20.2694 3.12262C18.1 1.0855 15.2207 -0.034466 12.24 0.000808666C7.70277 0.000808666 3.55359 2.55822 1.51648 6.61481L5.50252 9.70575C6.45052 6.86173 9.10935 4.74966 12.24 4.74966Z"
        fill={colorful ? '#EA4335' : 'currentColor'}
      />
    </svg>
  );
};

export const styles = sortCx({
  common: {
    root: 'group relative inline-flex h-max cursor-pointer items-center justify-center font-semibold whitespace-nowrap outline-focus-ring transition duration-100 ease-linear before:absolute focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:stroke-fg-disabled disabled:text-fg-disabled disabled:*:text-fg-disabled',
    icon: 'pointer-events-none shrink-0 transition-inherit-all',
  },

  sizes: {
    sm: {
      root: 'gap-2 rounded-lg px-3 py-2 text-sm before:rounded-[7px] data-icon-only:p-2',
    },
    md: {
      root: 'gap-2.5 rounded-lg px-3.5 py-2.5 text-sm before:rounded-[7px] data-icon-only:p-2.5',
    },
    lg: {
      root: 'gap-3 rounded-lg px-4 py-2.5 text-md before:rounded-[7px] data-icon-only:p-2.5',
    },
    xl: {
      root: 'gap-3.5 rounded-lg px-4.5 py-3 text-md before:rounded-[7px] data-icon-only:p-3.5',
    },
    '2xl': {
      root: 'gap-4 rounded-[10px] px-5.5 py-4 text-lg before:rounded-[9px] data-icon-only:p-4',
    },
  },

  colors: {
    gray: {
      root: 'bg-primary text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover',
      icon: 'text-fg-quaternary group-hover:text-fg-quaternary_hover',
    },
    black: {
      root: 'bg-black text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0%',
      icon: '',
    },

    facebook: {
      root: 'bg-[#1877F2] text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0% hover:bg-[#0C63D4]',
      icon: '',
    },

    dribble: {
      root: 'bg-[#EA4C89] text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset before:absolute before:inset-px before:border before:border-white/12 before:mask-b-from-0% hover:bg-[#E62872]',
      icon: '',
    },
  },
});

interface ICommonProps {
  social: 'google';
  disabled?: boolean;
  theme?: 'brand' | 'color' | 'gray';
  size?: keyof typeof styles.sizes;
}

interface IButtonProps
  extends
    ICommonProps,
    React.DetailedHTMLProps<Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'slot'>, HTMLButtonElement> {
  slot?: AriaButtonProps['slot'];
}

interface ILinkProps
  extends
    ICommonProps,
    React.DetailedHTMLProps<Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color'>, HTMLAnchorElement> {}

export type SocialButtonProps = IButtonProps | ILinkProps;

const SocialButton = ({
  size = 'lg',
  theme = 'brand',
  social,
  className,
  children,
  disabled,
  ...otherProps
}: SocialButtonProps) => {
  const href = 'href' in otherProps ? otherProps.href : undefined;
  const Component = href ? AriaLink : AriaButton;

  const isIconOnly = !children;

  const socialToColor = {
    google: 'gray',
  } as const;

  const colorStyles = theme === 'brand' ? styles.colors[socialToColor[social]] : styles.colors.gray;

  const logos = {
    google: GoogleLogo,
  };

  const Logo = logos[social];

  let props = {};

  if (href) {
    props = {
      ...otherProps,

      href: disabled ? undefined : href,

      // Since anchor elements do not support the `disabled` attribute and state,
      // we need to specify `data-rac` and `data-disabled` in order to be able
      // to use the `disabled:` selector in classes.
      ...(disabled ? { 'data-rac': true, 'data-disabled': true } : {}),
    };
  } else {
    props = {
      ...otherProps,

      type: otherProps.type || 'button',
      isDisabled: disabled,
    };
  }

  return (
    <Component
      isDisabled={disabled}
      {...props}
      data-icon-only={isIconOnly ? true : undefined}
      className={cx(styles.common.root, styles.sizes[size].root, colorStyles.root, className)}
    >
      <Logo
        className={cx(styles.common.icon, theme === 'gray' ? colorStyles.icon : '')}
        colorful={theme === 'brand' || theme === 'color'}
      />

      {children}
    </Component>
  );
};

export default SocialButton;
